import React, { ChangeEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Info,
  Palette,
  X,
  Image
} from "lucide-react";
import ProductVariantManager from "./ProductVariantManager";
import useToastStore from "../../store/useToastStore";
import { ProductService } from "../../services/productService";
import { Product } from "../../types/product";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import Quill styles


// Mock components for demonstration
const Loading = () => (
  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
);

type VariantOption = {
  id?: string; // Optional ID for existing variants
  attributes: Record<string, string>;
  mrp: number;
  discountPercent: number;
  stock: number;
  sku: string;
  existingImages: string[]; // Existing URLs from backend
  images: File[];           // New uploads
  discountedPrice: number;
};

type ProductVariantConfig = {
  hasVariants: boolean;
  attributes: string[];
  variantOptions: Record<string, string[]>;
  variants: VariantOption[];
};

type ProductForm = {
  title: string;
  description: string;
  mrp?: number;
  discountPercent?: number;
  stock?: number;
  images?: File[];
  hasVariants: boolean;
  variantConfig?: ProductVariantConfig;
};

const AddOrEditProductPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get("id");
  const isEditMode = !!productId;

  // Product loading state
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [productLoaded, setProductLoaded] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productImages, setProductImages] = useState<File[]>([]);

  // Variant system state
  const [variantConfig, setVariantConfig] = useState<ProductVariantConfig>({
    hasVariants: false,
    attributes: [],
    variantOptions: {},
    variants: []
  });

  // Toast store
  const { showToast } = useToastStore();

  const { register, handleSubmit, reset, control, watch, formState: { errors } } = useForm<ProductForm>({
    defaultValues: {
      title: "",
      description: "",
      mrp: undefined,
      discountPercent: undefined,
      stock: undefined,
      hasVariants: false,
    },
  });

  const formValues = watch();

  // Load product data for edit mode
  useEffect(() => {
    const loadProduct = async () => {
      if (!isEditMode || !productId || productLoaded) return;

      try {
        setIsLoading(true);
        const productData = await ProductService.getProductById(productId);
        setProduct(productData);

        // Set existing images
        if (productData.images) {
          setExistingImages(productData.images);
        }

        // Populate form with product data
        reset({
          title: productData.title,
          description: productData.description,
          mrp: productData.mrp,
          discountPercent: productData.discountPercent,
          stock: productData.stock,
          hasVariants: productData.hasVariants || false,
        });

        // Populate variant config if product has variants
        if (productData.hasVariants && productData.variants) {
          setVariantConfig({
            hasVariants: true,
            attributes: productData.attributes || [],
            variantOptions: productData.variantOptions || {},
            variants: productData.variants.map(variant => ({
              id: variant._id, // Map _id to id for frontend compatibility
              attributes: variant.attributes,
              mrp: variant.mrp, // Map price to mrp for form compatibility
              discountPercent: variant.discountPercent,
              stock: variant.stock,
              sku: variant.sku,
              existingImages: variant.images || [], // Load existing images from backend
              images: [], // New uploads start empty
              discountedPrice: variant.discountedPrice
            }))
          });
        }

      } catch (error) {
        console.error("Failed to load product:", error);
        showToast("Failed to load product. Please try again.", "error");
        navigate("/admin/products");
      } finally {
        setIsLoading(false);
        setProductLoaded(true);
      }
    };

    loadProduct();
  }, [isEditMode, productId, productLoaded, showToast, navigate]);

  // Helper function for generating variant combinations (used in onSubmit)
  const generateVariantCombinations = () => {
    const attributes = variantConfig.attributes;
    const options = variantConfig.variantOptions;

    if (attributes.length === 0) return [];

    const generateCombinations = (attrs: string[], current: Record<string, string> = {}): Record<string, string>[] => {
      if (attrs.length === 0) return [current];

      const [attr, ...rest] = attrs;
      const values = options[attr] || [];
      const combinations: Record<string, string>[] = [];

      values.forEach(value => {
        combinations.push(...generateCombinations(rest, { ...current, [attr]: value }));
      });

      return combinations;
    };

    return generateCombinations(attributes);
  };

  // Product image logic (for non-variant products)
  const handleProductImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      const maxSize = 5 * 1024 * 1024; // 5MB
      const oversizedFiles = files.filter(file => file.size > maxSize);
      if (oversizedFiles.length > 0) {
        showToast("Some images are too large. Maximum size is 5MB per image.", "error");
        return;
      }
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        showToast("Some files are not valid images. Please use JPG, PNG, or WebP files.", "error");
        return;
      }
      setProductImages([...productImages, ...files]);
    }
  };

  const removeProductImage = (index: number) => {
    setProductImages(productImages.filter((_, i) => i !== index));
  };

  // Remove existing image
  const removeExistingImage = (index: number) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: ProductForm) => {
    try {
      setIsSubmitting(true);

      console.log("data", data);

      // Validation
      const hasVariants = variantConfig.hasVariants;

      // Validate base fields for all products
      if (!data.mrp || data.mrp < 0) {
        showToast("Please enter a valid MRP.", "error");
        return;
      }
      if ((data.stock || 0) < 0) {
        showToast("Stock cannot be negative.", "error");
        return;
      }
      if ((data.discountPercent || 0) < 0 || (data.discountPercent || 0) > 100) {
        showToast("Discount must be between 0 and 100%.", "error");
        return;
      }

      console.log("hasVariants", hasVariants);

      // Generate the complete product document
      const slug = data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      let productDocument: any = {
        // Basic info
        id: productId || '',
        title: data.title,
        description: data.description,
        config: {},

        // SEO & control
        slug,
        status: "draft",
        visibility: "public"
      };

      if (hasVariants) {
        if (variantConfig.attributes.length === 0) {
          showToast("Please add at least one attribute.", "error");
          return;
        }

        const emptyAttributes = variantConfig.attributes.filter(attr =>
          !variantConfig.variantOptions[attr] || variantConfig.variantOptions[attr].length === 0
        );
        if (emptyAttributes.length > 0) {
          showToast(`Please add values for: ${emptyAttributes.join(', ')}`, "error");
          return;
        }

        if (variantConfig.variants.length === 0) {
          showToast("Please generate variant combinations first.", "error");
          return;
        }

        const invalidVariants = variantConfig.variants.filter(variant =>
          variant.mrp <= 0 || variant.stock < 0 || variant.discountPercent < 0 || variant.discountPercent > 100
        );
        if (invalidVariants.length > 0) {
          showToast("Please fill in all variant details (price, discount, and stock) correctly", "error");
          return;
        }

        // Generate combinations from the current variant config
        const combinations = generateVariantCombinations();

        // Process variants and calculate prices
        const processedVariants = combinations.map((combo, index) => {
          const existingVariant = variantConfig.variants.find(v =>
            Object.keys(combo).every(key => v.attributes[key] === combo[key])
          );

          console.log(`Processing variant ${index}:`, {
            combo,
            existingVariant: existingVariant ? {
              id: existingVariant.id,
              attributes: existingVariant.attributes,
              images: existingVariant.images?.length || 0
            } : 'Not found'
          });


          // Generate SKU
          const skuParts = Object.entries(combo).map(([key, value]) =>
            `${key.toUpperCase().substring(0, 3)}-${value.toUpperCase()}`
          );
          const sku = `PROD-${skuParts.join('-')}`;

          // Generate or preserve ID
          const variantId = existingVariant?.id || `variant_${Date.now()}_${index}`;

          return {
            id: variantId, // Include variant ID
            attributes: combo,
            mrp: existingVariant?.mrp !== undefined ? existingVariant.mrp : 0,
            stock: existingVariant?.stock !== undefined ? existingVariant.stock : 0,
            sku,
            existingImages: existingVariant?.existingImages || [], // Keep existing images
            images: existingVariant?.images || [], // Keep new uploads
            discountPercent: existingVariant?.discountPercent !== undefined ? existingVariant.discountPercent : 0
          };
        });


        productDocument = {
          ...productDocument,
          // Variant support
          hasVariants: true,
          attributes: variantConfig.attributes,
          variantOptions: variantConfig.variantOptions,
          variants: processedVariants,

          // Base product fields from form data
          mrp: data.mrp || 0,
          stock: data.stock || 0,
          discountPercent: data.discountPercent || 0,

          // Images (both existing URLs and new Files)
          images: [...existingImages, ...productImages]
        };
      } else {
        console.log("simple product");
        if (productImages.length === 0 && existingImages.length === 0) {
          showToast("Please add at least one product image.", "error");
          return;
        }
        // Simple product without variants
        const mrp = data.mrp || 0;
        const discountPercent = data.discountPercent || 0;

        productDocument = {
          ...productDocument,
          // Simple product fallback
          hasVariants: false,
          mrp,
          stock: data.stock || 0,
          sku: `PROD-${data.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').toUpperCase()}`,
          discountPercent,

          // Images (both existing URLs and new Files)
          images: [...existingImages, ...productImages]
        };
      }

      // Call the product service to add or update the product
      const result = await ProductService.addOrUpdateProduct({
        data: productDocument
      });

      showToast(`Product ${isEditMode ? "updated" : "added"} successfully`, "success");
      navigate("/admin/products");

    } catch (error) {
      console.error("Form submission error:", error);
      showToast("Failed to save product. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading />
          <p className="mt-4 text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white pb-24">
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <form className="space-y-10">
          {/* Product Details Section */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6 relative">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <h2 className="text-2xl font-bold text-gray-900">Product Details</h2>
            </div>
            <p className="text-gray-500 text-sm mb-4">Enter the basic information about your product.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
                <input
                  type="text"
                  placeholder="e.g., Classic T-shirt"
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.title ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                  {...register("title", { required: "Product name is required" })}
                  aria-invalid={!!errors.title}
                  aria-describedby="product-name-help"
                />
                <p id="product-name-help" className="text-xs text-gray-400 mt-1">This will be shown to customers.</p>
                {errors.title && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.title.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Description is required",
                    validate: (value) => {
                      const plainText = value.replace(/<[^>]+>/g, '');
                      return plainText.trim().length > 0 || "Description cannot be empty";
                    }
                  }}
                  render={({ field }) => (
                    <div>
                      <ReactQuill
                        theme="snow"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Describe your product..."
                        className={`bg-white ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                      />
                    </div>
                  )}
                />
                <p id="product-desc-help" className="text-xs text-gray-400 mt-1">A good description helps customers decide.</p>
                {errors.description && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><Info className="w-4 h-4" />{errors.description.message}</p>
                )}
              </div>
            </div>
          </section>


          {/* Pricing & Inventory Section */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-6 h-6 text-yellow-500" />
              <h3 className="text-xl font-bold text-gray-900">Pricing & Inventory</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">Set the price, discount, and available stock for this product.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MRP (₹)</label>
                <Controller
                  name="mrp"
                  control={control}
                  rules={{
                    required: "MRP is required",
                    min: { value: 0, message: "MRP cannot be negative" }
                  }}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="0.00"
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.mrp ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                      inputMode="decimal"
                      step="0.01"
                      value={field.value === 0 ? "" : field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          field.onChange(undefined);
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? undefined : numValue);
                        }
                      }}
                      onWheel={e => (e.target as HTMLInputElement).blur()}
                      aria-invalid={!!errors.mrp}
                      aria-describedby="mrp-help"
                    />
                  )}
                />
                <p id="mrp-help" className="text-xs text-gray-400 mt-1">Maximum Retail Price.</p>
                {errors.mrp && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><Info className="w-4 h-4" />{errors.mrp.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                <Controller
                  name="discountPercent"
                  control={control}
                  rules={{
                    required: "Discount is required",
                    min: { value: 0, message: "Discount cannot be negative" },
                    max: { value: 100, message: "Discount cannot exceed 100%" }
                  }}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="0"
                      className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 ${errors.discountPercent ? 'border-red-300 ring-red-100 focus:border-red-500 focus:ring-red-200' : 'border-gray-300'}`}
                      inputMode="decimal"
                      step="0.1"
                      value={field.value === 0 ? "" : field.value || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          field.onChange(undefined);
                        } else {
                          const numValue = parseFloat(value);
                          field.onChange(isNaN(numValue) ? undefined : numValue);
                        }
                      }}
                      onWheel={e => (e.target as HTMLInputElement).blur()}
                      aria-invalid={!!errors.discountPercent}
                      aria-describedby="discount-help"
                    />
                  )}
                />
                <p id="discount-help" className="text-xs text-gray-400 mt-1">Leave 0 for no discount.</p>
                {errors.discountPercent && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><Info className="w-4 h-4" />{errors.discountPercent.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inventory Quantity</label>
                <Controller
                  name="stock"
                  control={control}
                  rules={{
                    required: "Inventory is required",
                    min: { value: 0, message: "Inventory cannot be negative" }
                  }}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="Enter quantity"
                      value={field.value === 0 ? "" : field.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          field.onChange(0);
                        } else {
                          const numValue = parseInt(value);
                          field.onChange(isNaN(numValue) ? 0 : numValue);
                        }
                      }}
                      className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-200 focus:border-blue-500 bg-gray-50 transition-all duration-200 border-gray-300"
                      min="0"
                      onWheel={e => (e.target as HTMLInputElement).blur()}
                      aria-invalid={!!errors.stock}
                      aria-describedby="inventory-help"
                    />
                  )}
                />
                <p id="inventory-help" className="text-xs text-gray-400 mt-1">How many items are in stock?</p>
              </div>
            </div>
          </section>

          {/* Product Images Section */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Image className="w-6 h-6 text-green-500" />
              <h3 className="text-xl font-bold text-gray-900">Product Images</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">Upload high-quality images to showcase your product.</p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 transition bg-gray-50 mb-4"
              onClick={() => document.getElementById('product-image-input')?.click()}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={e => {
                e.preventDefault();
                e.stopPropagation();
                const files = Array.from(e.dataTransfer.files);
                const event = { target: { files } } as unknown as React.ChangeEvent<HTMLInputElement>;
                handleProductImageChange(event);
              }}
              tabIndex={0}
              aria-label="Upload product images"
            >
              <Image className="w-10 h-10 text-gray-400 mb-2" />
              <span className="text-gray-500 text-sm">Drag & drop images here, or <span className="text-blue-600 underline">click to upload</span></span>
              <input
                id="product-image-input"
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleProductImageChange}
              />
            </div>
            {(existingImages.length > 0 || productImages.length > 0) && (
              <div className="space-y-4">
                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Images</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {existingImages.map((imageUrl, index) => (
                        <div key={`existing-${index}`} className="relative group rounded-lg overflow-hidden shadow border border-gray-200 bg-white">
                          <img
                            src={imageUrl}
                            alt={`Existing ${index}`}
                            className="object-cover w-full h-32 sm:h-40 transition-transform duration-300 group-hover:scale-105"
                          />
                          <button
                            type="button"
                            className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                            onClick={e => { e.stopPropagation(); removeExistingImage(index); }}
                            aria-label="Remove existing image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Images */}
                {productImages.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">New Images</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {productImages.map((image, index) => (
                        <div key={`new-${index}`} className="relative group rounded-lg overflow-hidden shadow border border-gray-200 bg-white">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`New ${index}`}
                            className="object-cover w-full h-32 sm:h-40 transition-transform duration-300 group-hover:scale-105"
                          />
                          <button
                            type="button"
                            className="absolute top-2 right-2 text-white bg-red-500 hover:bg-red-600 rounded-full p-1 opacity-80 group-hover:opacity-100 transition-all duration-200 shadow"
                            onClick={e => { e.stopPropagation(); removeProductImage(index); }}
                            aria-label="Remove new image"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Product Variant Manager */}
          <section className="bg-white rounded p-6 border border-gray-100 space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-6 h-6 text-pink-500" />
              <h3 className="text-xl font-bold text-gray-900">Product Variants</h3>
            </div>
            <p className="text-gray-500 text-sm mb-4">Add options like size or color to create product variants.</p>

            <ProductVariantManager
              variantConfig={variantConfig}
              onVariantConfigChange={(config: ProductVariantConfig) => { setVariantConfig(config); console.log("variantConfig", config) }}
              toast={{ showToast }}
            />
          </section>
        </form>
      </div>

      {/* Fixed Action Buttons at Bottom */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-lg z-50 p-2 sm:p-4 lg:left-64 lg:w-[calc(100%-16rem)]">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-base text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg sm:rounded-xl transition duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
              className="flex-1 px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-base bg-blue-600 text-white rounded-lg sm:rounded-xl hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <Loading />
                  <span className="ml-1 sm:ml-2 text-xs sm:text-sm">Saving...</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <span className="text-xs sm:text-sm">Save {isEditMode ? "Changes" : "Product"}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOrEditProductPage;
